// Components/CompanyForm.tsx

import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  AdvancedSearchMetadataIndustry,
  CompanyFormData,
} from '../../../../Types/AdvanceSearchResultsTypes';
import { actions } from '../../../../Slice/AdvanceSearchSlice';
import {
  selectAdvanceSearchMetadata,
  selectAdvanceSearchCompanyForm,
} from '../../../../Selector/AdvanceSearchSelector';
import {
  buildAdvanceSearchRegionOptions,
  buildAdvanceSearchCountryOptions,
  getAdvanceSearchRegionForCountry,
} from '../../../../Utils/UtilityFunctions';
import { FormField } from './FormField';

const FormContainer = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

interface CompanyFormProps {
  isLoading?: boolean;
}

export const CompanyForm: React.FC<CompanyFormProps> = ({ isLoading }) => {
  const dispatch = useDispatch();
  const filters = useSelector(selectAdvanceSearchMetadata);
  const formData = useSelector(selectAdvanceSearchCompanyForm);
  const [industryId, setIndustryId] = useState(0);
  const [revenueId, setRevenueId] = useState(0);
  const [sizeId, setSizeId] = useState(0);
  const industryOptions = useMemo(
    () => (filters?.industries ?? []).map((ind: AdvancedSearchMetadataIndustry) => ({ id: ind.id, label: ind.name })),
    [filters?.industries],
  );
  const revenueOptions = useMemo(
    () => (filters?.companyRevenue ?? []).map((label: string, i: number) => ({ id: i + 1, label })),
    [filters?.companyRevenue],
  );
  const companySizeOptions = useMemo(
    () => (filters?.companySizes ?? []).map((label: string, i: number) => ({ id: i + 1, label })),
    [filters?.companySizes],
  );
  const regionOptions = useMemo(
    () => buildAdvanceSearchRegionOptions(filters?.regions),
    [filters?.regions],
  );
  const countryOptions = useMemo(
    () => buildAdvanceSearchCountryOptions(filters, formData.region),
    [filters, formData.region],
  );
  const countryId = useMemo(
    () => countryOptions.find((option) => option.label === formData.country)?.id ?? 0,
    [countryOptions, formData.country],
  );
  const regionId = useMemo(
    () => regionOptions.find((option) => option.label === formData.region)?.id ?? 0,
    [regionOptions, formData.region],
  );

  const handleIndustryChange = (id: number) => {
    setIndustryId(id);
    const selectedLabel = industryOptions.find((option) => option.id === id)?.label ?? '';
    updateField('industry', selectedLabel);
  };

  const handleRevenueChange = (id: number) => {
    setRevenueId(id);
    const selectedLabel = revenueOptions.find((option) => option.id === id)?.label ?? '';
    updateField('revenue', selectedLabel);
  };

  const handleCountryChange = (id: number) => {
    if (!id) {
      updateFields({ country: '' });
      return;
    }
    const selectedLabel = countryOptions.find((option) => option.id === id)?.label ?? '';
    updateFields({ country: selectedLabel, region: getAdvanceSearchRegionForCountry(filters, selectedLabel) });
  };

  const handleRegionChange = (id: number) => {
    if (!id) {
      updateFields({ region: '' });
      return;
    }
    const selectedLabel = regionOptions.find((option) => option.id === id)?.label ?? '';
    if (selectedLabel === formData.region) return;
    updateFields({ region: selectedLabel, country: '' });
  };

  const handleSizeChange = (id: number) => {
    setSizeId(id);
    const selectedLabel = companySizeOptions.find((option) => option.id === id)?.label ?? '';
    updateField('size', selectedLabel);
  };

  const updateField = (field: keyof CompanyFormData, value: string | number) => {
    const newData = { ...formData, [field]: value };
    dispatch(actions.setAdvanceSearchCompanyForm(newData));
  };

  const updateFields = (partial: Partial<CompanyFormData>) => {
    dispatch(actions.setAdvanceSearchCompanyForm({ ...formData, ...partial }));
  };

  useEffect(() => {
    const matchedId = companySizeOptions.find((option) => option.label === formData.size)?.id ?? 0;
    setSizeId(matchedId);
  }, [companySizeOptions, formData.size]);

  useEffect(() => {
    const matchedId = industryOptions.find((option) => option.label === formData.industry)?.id ?? 0;
    setIndustryId(matchedId);
  }, [formData.industry, industryOptions]);

  useEffect(() => {
    const matchedId = revenueOptions.find((option) => option.label === formData.revenue)?.id ?? 0;
    setRevenueId(matchedId);
  }, [formData.revenue, revenueOptions]);

  return (
    <FormContainer>
      <FormField
        label="Name"
        value={formData.name}
        onChange={(val) => updateField('name', val)}
        placeholder="Enter company name"
      />
      <FormField
        label="Industry"
        type="dropdown"
        value={industryId}
        onChange={(val) => handleIndustryChange(val as number)}
        options={industryOptions}
        placeholder="Select industry"
        searchPlaceholder="Search industry"
        hasSearch={true}
        isLoading={isLoading}
      />
      <FormField
        label="Revenue"
        type="dropdown"
        value={revenueId}
        onChange={(val) => handleRevenueChange(val as number)}
        options={revenueOptions}
        placeholder="Select revenue range"
        isLoading={isLoading}
      />
      <FormField
        label="Country"
        type="dropdown"
        value={countryId}
        onChange={(val) => handleCountryChange(val as number)}
        options={countryOptions}
        placeholder="Select country"
        hasSearch={true}
        isLoading={isLoading}
      />
      <FormField
        label="Region"
        type="dropdown"
        value={regionId}
        onChange={(val) => handleRegionChange(val as number)}
        options={regionOptions}
        placeholder="Select region"
        isLoading={isLoading}
      />
      <FormField
        label="Size"
        type="dropdown"
        value={sizeId}
        onChange={(val) => handleSizeChange(val as number)}
        options={companySizeOptions}
        placeholder="Select company size"
        isLoading={isLoading}
      />
    </FormContainer>
  );
};